import React, { PureComponent } from 'react';
import { Card, CardContent, Typography, Box, Skeleton, Button, Collapse, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withStyles } from '@mui/styles';
import { marked } from 'marked';
import { UrlBuilder } from "../../core/url/UrlBuilder";
import AiChatButton from './AiChatButton';

const styles = theme => ({
    card: {
        borderColor: theme.palette.secondary.main,
        borderWidth: 1,
    },
    content: {
        padding: `${theme.spacing(1.5)} !important`,
        '& p': { margin: '4px 0' },
        '& ul': { margin: '4px 0', paddingLeft: 20 },
        '& li': { marginBottom: 2 },
    },
});

class WorkoutInsightCard extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            insightText: '',
            loading: false,
            visible: false,
            chatId: null,
            creditsRemaining: null,
            expanded: !!props.showWorkoutDoneMessage,
        };
        this.streamBuffer = '';
        this.rafId = null;
    }

    componentDidMount() {
        this.fetchInsight();
    }

    componentWillUnmount() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    fetchInsight = async () => {
        const canGenerate = this.props.showWorkoutDoneMessage;
        this.setState({ loading: canGenerate, visible: canGenerate });

        try {
            const url = UrlBuilder.ai.workoutInsight(this.props.workoutId) + (canGenerate ? '?generate=true' : '');
            const response = await fetch(url);

            if (response.status === 402 || response.status === 401) {
                this.setState({ loading: false, visible: false });
                return;
            }

            const contentType = response.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
                const data = await response.json();
                if (!data.hasInsight && !data.cached) {
                    this.setState({ loading: false, visible: false });
                    return;
                }
                if (data.cached && data.content) {
                    this.setState({ loading: false, visible: true, insightText: data.content, chatId: data.chatId, creditsRemaining: data.creditsRemaining });
                    return;
                }
                this.setState({ loading: false, visible: false });
                return;
            }

            await this.consumeSseStream(response);
        } catch {
            this.setState({ loading: false, visible: false });
        }
    };

    bufferStreamChunk = (text) => {
        this.streamBuffer += text;
        if (!this.rafId) {
            this.rafId = requestAnimationFrame(this.flushStreamBuffer);
        }
    };

    flushStreamBuffer = () => {
        this.rafId = null;
        if (!this.streamBuffer) return;
        const toAppend = this.streamBuffer;
        this.streamBuffer = '';
        this.setState(prev => ({ insightText: prev.insightText + toAppend }));
    };

    consumeSseStream = async (response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let hasStartedStreaming = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();

            for (const part of parts) {
                const dataLine = part.split('\n').find(l => l.startsWith('data: '));
                if (!dataLine) continue;

                let parsed;
                try { parsed = JSON.parse(dataLine.substring(6)); } catch { continue; }

                if (parsed.type === 'chunk') {
                    if (!hasStartedStreaming) {
                        hasStartedStreaming = true;
                        this.setState({ loading: false, insightText: parsed.text });
                    } else {
                        this.bufferStreamChunk(parsed.text);
                    }
                } else if (parsed.type === 'done') {
                    if (this.rafId) {
                        cancelAnimationFrame(this.rafId);
                        this.rafId = null;
                    }
                    this.flushStreamBuffer();
                    this.setState({ chatId: parsed.chatId, creditsRemaining: parsed.creditsRemaining });
                } else if (parsed.type === 'error') {
                    this.setState({ loading: false, visible: false });
                    return;
                }
            }
        }
    };

    toggleExpanded = () => {
        this.setState(prev => ({ expanded: !prev.expanded }));
    };

    render() {
        const { classes } = this.props;
        const { visible, loading, insightText, creditsRemaining, expanded } = this.state;

        if (!visible) return null;

        return (
            <Box py={1.5}>
                <Card variant="outlined" className={classes.card}>
                    <CardContent className={classes.content}>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.75}
                            sx={{ cursor: loading ? 'default' : 'pointer' }}
                            onClick={loading ? undefined : this.toggleExpanded}
                        >
                            <AutoAwesomeIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                            <Typography variant="subtitle2" color="secondary">
                                Workout Insight
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                                AI-powered{creditsRemaining != null ? ` · ${creditsRemaining} credit${creditsRemaining !== 1 ? 's' : ''} left` : ''}
                            </Typography>
                            {!loading && (
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                    <ExpandMoreIcon sx={{
                                        fontSize: 18,
                                        transition: 'transform 0.2s',
                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }} />
                                </IconButton>
                            )}
                        </Box>

                        {loading ? (
                            <Box mt={0.5}>
                                <Skeleton animation="wave" variant="text" width="90%" />
                                <Skeleton animation="wave" variant="text" width="75%" />
                                <Skeleton animation="wave" variant="text" width="60%" />
                            </Box>
                        ) : (
                            <Collapse in={expanded}>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ '& h1,& h2,& h3,& h4': { fontSize: '0.95rem', fontWeight: 600, mt: 1, mb: 0.25 } }}
                                    dangerouslySetInnerHTML={{ __html: marked.parse(insightText || '') }}
                                />
                                {this.state.chatId && (
                                    <Box display="flex" justifyContent="center">
                                    <AiChatButton
                                        triggerComponent={
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="small"
                                                startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                                sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
                                            >
                                                Ask a follow-up
                                            </Button>
                                        }
                                        initialChatId={this.state.chatId}
                                        initialMessage={insightText}
                                        pageContext={`workout/${this.props.workoutId}`}
                                        presetSuggestions={[
                                            "How can I improve next time?",
                                            "What should I focus on?",
                                            "Break down my performance",
                                            "Set a new goal based on this workout",
                                        ]}
                                    />
                                    </Box>
                                )}
                            </Collapse>
                        )}
                    </CardContent>
                </Card>
            </Box>
        );
    }
}

export default withStyles(styles)(WorkoutInsightCard);
